import { Injectable } from '@angular/core';
import { extractPdfText } from '../../../utils/pdf-text';

import { DebitCredit, StatementPreview, StatementTrade, TradeSide } from './statement-reader.model';

// Brazilian formatted numbers, e.g. 500 / 6.395,00 / 12,79
const INTEGER = String.raw`\d+(?:\.\d{3})*`;
const DECIMAL = String.raw`\d+(?:\.\d{3})*,\d+`;
const DATE = String.raw`\d{2}\/\d{2}\/\d{4}`;

// B3 RV LISTADO C VISTA EZTEC ON NM @ 500 12,79 6.395,00 D
// pdf.js may or may not split "LISTADO" and the C/V column into separate items
const TRADE_REGEX = new RegExp(
  String.raw`B3\s*RV\s*LISTADO\s*([CV])\s*(VISTA|FRACIONARIO|OPCAO DE COMPRA|OPCAO DE VENDA|TERMO)\s+((?:(?!B3\s*RV).)+?)\s+(${INTEGER})\s+(${DECIMAL})\s+(${DECIMAL})\s+([DC])\b`,
  'g'
);

// Observation codes (legend "(*) - Observações") that can trail the title: @, #, 2, 8, D, H...
const OBSERVATION_TOKEN = /^(?:[@#*28]+[A-Z]?|[A-Z])$/;

@Injectable({
  providedIn: 'root'
})
export class GenialLayoutService {

  constructor() { }

  extractTextFromPDF(file: File): Promise<string> {
    return extractPdfText(file);
  }

  parse(rawText: string): StatementPreview {
    // Normalize whitespace so line breaks between pdf.js items don't matter
    const text = rawText.replace(/\s+/g, ' ');

    const header = this.parseHeader(text);
    const net = text.match(new RegExp(String.raw`L[íi]quido para\s*(${DATE})\s*(${DECIMAL})\s*([DC])?`));

    return {
      noteNumber: header.noteNumber,
      date: header.date,
      trades: this.parseTrades(text),
      totalOperations: this.amount(text, String.raw`Valor das operações`),
      liquidationTax: this.amount(text, String.raw`Taxa de liquidação(?:\/CCP)?`),
      registrationTax: this.amount(text, String.raw`Taxa de Registro`),
      emoluments: this.amount(text, String.raw`Emolumentos`),
      transferTax: this.amount(text, String.raw`Taxa de Transferência de Ativos`),
      clearing: this.amount(text, String.raw`Clearing`),
      iss: this.amount(text, String.raw`ISS\s*\([^)]*\)`),
      issPisCofins: this.amount(text, String.raw`ISS\/PIS\/COFINS`),
      totalExpenses: this.amount(text, String.raw`Total corretagem \/ Despesas`),
      settlementDate: net?.[1] ?? '',
      netValue: parseBrazilianNumber(net?.[2]),
      netDebitCredit: (net?.[3] as DebitCredit) ?? ''
    };
  }

  // pdf.js: "Data pregão Folha Nr.Nota 14/09/2026 1 10648"
  // PDF viewers: "Nr.Nota Folha Data pregão 10648 1 14/09/2026"
  private parseHeader(text: string): { noteNumber: string, date: string } {
    const reversed = text.match(new RegExp(String.raw`Data preg[ãa]o\s+Folha\s+Nr\.?\s*Nota\s+(${DATE})\s+\d+\s+(\d+)`));
    if (reversed) return { noteNumber: reversed[2], date: reversed[1] };

    const ordered = text.match(new RegExp(String.raw`Data preg[ãa]o\s+(?:(\d+)\s+\d+\s+)?(${DATE})`));
    return { noteNumber: ordered?.[1] ?? '', date: ordered?.[2] ?? '' };
  }

  private parseTrades(text: string): StatementTrade[] {
    const trades: StatementTrade[] = [];

    for (const match of text.matchAll(TRADE_REGEX)) {
      const tokens = match[3].trim().split(' ');
      const observation: string[] = [];
      while (tokens.length > 1 && OBSERVATION_TOKEN.test(tokens[tokens.length - 1])) {
        observation.unshift(tokens.pop()!);
      }

      trades.push({
        side: match[1] as TradeSide,
        market: match[2],
        title: tokens.join(' '),
        observation: observation.join(' '),
        quantity: parseBrazilianNumber(match[4]),
        price: parseBrazilianNumber(match[5]),
        total: parseBrazilianNumber(match[6]),
        debitCredit: match[7] as DebitCredit
      });
    }

    return trades;
  }

  // First value after the label; pdf.js may put the D/C column before it
  // e.g. "Emolumentos 0,72 D" or "Emolumentos D 0,72" -> 0.72
  private amount(text: string, label: string): number {
    const match = text.match(new RegExp(String.raw`${label}\s*(?:[DC]\s+)?(${DECIMAL})`));
    return parseBrazilianNumber(match?.[1]);
  }
}

export function parseBrazilianNumber(value: string | undefined): number {
  if (!value) return 0;
  const parsed = Number(value.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}
